import type { EnqueueAction } from '../types/analytics';
import type {
  ActionPayload,
  GlobalContext,
  PageContextInput,
  PageFields,
  PropertyContextInput,
  OutboundDigitalData,
  UserContextInput,
  UserFields,
} from '../types/digital-data';
import { applyPathPatch } from './digital-data-patch';
import { overlayAuthoritativeGlobals } from './digital-data-sanitizer';

const PAGE_OWNED_KEYS = [
  'siteName',
  'languageCode',
  'brandCode',
  'primaryCategory',
  'pageType',
  'pageDetail1',
  'pageDetail2',
  'pageDetail3',
  'pageTitle',
  'version',
] as const satisfies ReadonlyArray<keyof Omit<PageFields, 'loginStatus'>>;

const USER_OWNED_KEYS = [
  'profileID',
  'rewardsTier',
  'userType',
  'segment',
] as const satisfies ReadonlyArray<keyof UserFields>;

export class DigitalDataComposer {
  private baselinePayload: ActionPayload = { event: [] };
  private authoritativeGlobal: GlobalContext = { page: { loginStatus: 'logged-out' } };
  private lastStrippedForbiddenPaths: string[] = [];

  setBaselineDigitalData(payload: ActionPayload): void {
    this.baselinePayload = structuredClone(payload);
  }

  private getPageFields(ctx: PageContextInput): Partial<PageFields> {
    return PAGE_OWNED_KEYS.reduce<Partial<PageFields>>((page, key) => {
      const value = ctx[key];
      if (value !== undefined) {
        page[key] = value;
      }
      return page;
    }, {});
  }

  setPageContext(ctx: PageContextInput): void {
    const global = this.getAuthoritativeGlobal();

    global.page = { loginStatus: global.page.loginStatus, ...this.getPageFields(ctx) };
    this.authoritativeGlobal = global;
  }

  setUserContext(ctx: UserContextInput): void {
    const global = this.getAuthoritativeGlobal();
    global.page = {
      ...global.page,
      loginStatus: ctx.loginStatus,
    };

    if (ctx.loginStatus === 'logged-out') {
      const segment = ctx.segment ?? global.user?.segment;
      if (segment) {
        global.user = { segment };
      } else {
        delete global.user;
      }
    } else {
      const nextUser: UserFields = {};
      for (const key of USER_OWNED_KEYS) {
        const value = ctx[key];
        if (value !== undefined && value !== '') {
          nextUser[key] = value;
        }
      }
      global.user = nextUser;
    }

    this.authoritativeGlobal = global;
  }

  setPropertyContext(ctx: PropertyContextInput | null): void {
    const global = this.getAuthoritativeGlobal();

    if (ctx === null) {
      delete global.property;
    } else {
      global.property = structuredClone(ctx);
    }

    this.authoritativeGlobal = global;
  }

  getLastStrippedForbiddenPaths(): string[] {
    return [...this.lastStrippedForbiddenPaths];
  }

  composeDigitalData(action: EnqueueAction): OutboundDigitalData {
    this.lastStrippedForbiddenPaths = [];
    const authoritativeGlobal = this.getAuthoritativeGlobal();
    const composed: ActionPayload = structuredClone(this.baselinePayload);

    if (action.pathPatch) {
      this.lastStrippedForbiddenPaths = applyPathPatch(composed, action.pathPatch);
    }

    return overlayAuthoritativeGlobals(composed, authoritativeGlobal);
  }

  private getAuthoritativeGlobal(): GlobalContext {
    return structuredClone(this.authoritativeGlobal);
  }
}
